"use client";

import { useEffect, useState } from "react";
import { Navbar } from "./Navbar";
import { BottomNav } from "./BottomNav";
import { LayoutShell } from "./LayoutShell";
import { createClient } from "@/lib/supabase";
import { usePathname } from "next/navigation";
import type { UserRole } from "@/lib/auth";

interface AuthState {
  role: UserRole;
  userName?: string;
  userPhotoUrl?: string;
  unreadCount: number;
}

const LOGGED_OUT: AuthState = { role: null, unreadCount: 0 };

/**
 * Client-side auth chrome. The root layout must stay free of cookies()
 * so every page can render as static HTML (see CLAUDE.md gotchas); the
 * navbar discovers the session in the browser instead. Logged-out
 * visitors and crawlers make zero network calls. Logged-in users fetch
 * /api/me once per page view, which also refreshes their auth tokens.
 *
 * `footer` and `children` are passed through from the server layout, so
 * they remain server-rendered despite this being a client component.
 */
export function AppChrome({
  footer,
  children,
}: {
  footer: React.ReactNode;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const [auth, setAuth] = useState<AuthState>(LOGGED_OUT);

  useEffect(() => {
    let cancelled = false;
    let version = 0;
    let authTimer: ReturnType<typeof setTimeout> | undefined;
    const supabase = createClient();
    const controller = new AbortController();

    async function load() {
      const requestVersion = ++version;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled || requestVersion !== version) return;
        if (!session) { setAuth(LOGGED_OUT); return; }
        const res = await fetch("/api/me", { signal: controller.signal, cache: "no-store" });
        if (!res.ok || cancelled || requestVersion !== version) return;
        const me = (await res.json()) as AuthState;
        if (!cancelled && requestVersion === version) setAuth(me);
      } catch { /* Preserve the current navigation during a connection failure. */ }
    }
    void load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) { version++; setAuth(LOGGED_OUT); }
      else if (event === "SIGNED_IN") {
        // Supabase holds its session lock while delivering the callback.
        clearTimeout(authTimer);
        authTimer = setTimeout(() => { if (!cancelled) void load(); }, 0);
      }
    });
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(authTimer);
      subscription.unsubscribe();
    };
  }, [pathname]);

  return (
    <LayoutShell
      navbar={
        <Navbar
          userRole={auth.role}
          unreadCount={auth.unreadCount}
          userName={auth.userName}
          userPhotoUrl={auth.userPhotoUrl}
        />
      }
      footer={footer}
      bottomNav={<BottomNav userRole={auth.role} unreadCount={auth.unreadCount} />}
      hasBottomNav={!!auth.role}
    >
      {children}
    </LayoutShell>
  );
}
